// src/api/image.network.ts
import axios from 'axios';
import { imageHttp } from './imageHttp.ts';
import { ENDPOINTS } from './endpoints.ts';

// presign 이 허용하는 MIME (서버 ImageUploadController::PRESIGN_ALLOWED_MIME 와 동일)
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
};

function resolveMime(file: File): string {
  if (ALLOWED_MIME.includes(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  const byExt = EXT_MIME[ext];
  if (byExt) return byExt;
  throw new Error(`지원하지 않는 이미지 형식입니다: ${file.type || file.name}`);
}

/**
 * presigned URL 방식 업로드 (레거시 프록시 /upload 대체).
 *
 *  1. 서버에서 presigned PUT URL 발급
 *  2. 그 URL 로 S3 에 바이너리 직접 PUT (서버 경유 X)
 *  3. 소비 측 계약 유지: { raw, url } 반환
 *
 * `url` 은 preview_url(서명된 GET)이다 — 임시 오브젝트가 private 이라 bare
 * s3_url 은 렌더 시 403 이지만, preview_url 은 200 으로 뜨고 저장 시 그대로
 * 넘겨도 백엔드가 경로의 파일명만 보고 query string 은 무시한다(서버 계약).
 */
async function postImage(file: File) {
  const mime = resolveMime(file);

  // 1) presigned PUT URL 발급
  const res = await imageHttp.post(
    ENDPOINTS.image.presign,
    { items: [{ mime_type: mime, file_name: file.name }] },
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
  );

  const item = res.data?.items?.[0];
  if (!item?.upload_url) {
    throw new Error(`presign 에서 URL 을 받지 못했습니다: ${JSON.stringify(res.data)}`);
  }

  // 2) S3 로 바이너리 직접 PUT. 서명된 헤더(Content-Type)를 그대로 전송해야
  //    S3 가 서명을 검증한다. baseURL·인터셉터 없는 순수 axios 로 격리.
  await axios.put(item.upload_url, file, {
    headers: { ...(item.headers ?? {}), 'Content-Type': mime },
  });

  const url = item.preview_url ?? item.s3_url ?? null;
  return { raw: res.data, url };
}

export async function uploadProfileImage(file: File) {
  return postImage(file);
}

export async function uploadCommunityImage(file: File) {
  return postImage(file);
}

export async function uploadReviewImage(file: File) {
  return postImage(file);
}
